import { useEffect, useState } from "react";
import type { InsightSection } from "@/content/insights/types";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 64);
}

export function sectionId(section: InsightSection, idx: number): string {
  return `s-${idx}-${slugify(section.h2)}`;
}

interface Props {
  sections: InsightSection[];
}

/**
 * Sticky right-rail Table of Contents (desktop only). Highlights active
 * section via IntersectionObserver. Provides crawlable internal anchors
 * which improve dwell time + "Jump to" search snippets.
 */
export function ArticleTOC({ sections }: Props) {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const ids = sections.map((s, i) => sectionId(s, i));
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((e): e is HTMLElement => e !== null);
    if (els.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    els.forEach((e) => obs.observe(e));
    return () => obs.disconnect();
  }, [sections]);

  if (sections.length < 2) return null;

  return (
    <nav
      aria-label="สารบัญบทความ"
      className="sticky top-24 hidden max-h-[calc(100vh-8rem)] overflow-auto lg:block"
    >
      <div className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        สารบัญ
      </div>
      <ol className="space-y-2 border-l border-border">
        {sections.map((s, i) => {
          const id = sectionId(s, i);
          const isActive = active === id;
          return (
            <li key={id}>
              <a
                href={`#${id}`}
                className={`block border-l-2 pl-3 -ml-px text-xs leading-relaxed transition-colors ${
                  isActive
                    ? "border-gold font-bold text-gold"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {s.h2}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
